import type { LoginResponse, User } from "./auth.types";
import { mapAccessedModule } from "./moduleAccess";

function parseUserRecord(userRaw: Record<string, unknown>): User {
	return {
		id: typeof userRaw.id === "string" ? userRaw.id : "",
		email: typeof userRaw.email === "string" ? userRaw.email : "",
		name:
			(typeof userRaw.name === "string" && userRaw.name) ||
			(typeof userRaw.displayName === "string" && userRaw.displayName) ||
			"",
		accessedModules: Array.isArray(userRaw.accessedModules)
			? userRaw.accessedModules
					.map((entry) => mapAccessedModule(entry))
					.filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
			: [],
	};
}

export function parseLoginResponse(raw: unknown): LoginResponse {
	const root = (raw ?? {}) as Record<string, unknown>;
	const payload =
		root.data && typeof root.data === "object"
			? (root.data as Record<string, unknown>)
			: root;

	const accessToken =
		(typeof payload.accessToken === "string" && payload.accessToken) ||
		(typeof payload.token === "string" && payload.token) ||
		"";

	const refreshToken =
		typeof payload.refreshToken === "string" ? payload.refreshToken : "";

	const userRaw =
		payload.user && typeof payload.user === "object"
			? (payload.user as Record<string, unknown>)
			: payload;

	const user =
		typeof userRaw === "object" && userRaw !== null
			? parseUserRecord(userRaw as Record<string, unknown>)
			: parseUserRecord({});

	return { accessToken, refreshToken, user };
}

export interface AuthCallbackParams {
	code?: string;
	error?: string;
}

function readParam(
	params: URLSearchParams,
	keys: string[],
): string | undefined {
	for (const key of keys) {
		const value = params.get(key)?.trim();
		if (value) return value;
	}
	return undefined;
}

function parseParamsString(paramsString: string): AuthCallbackParams {
	const params = new URLSearchParams(paramsString);
	const code = readParam(params, ["code"]);
	const error =
		readParam(params, ["error", "error_description"]) ||
		(!code ? readParam(params, ["message"]) : undefined);

	return { code, error };
}

export function parseAuthCallbackFromLocation(
	location: Pick<Location, "search" | "hash">,
): AuthCallbackParams {
	const fromSearch = parseParamsString(
		location.search.startsWith("?")
			? location.search.slice(1)
			: location.search,
	);

	if (fromSearch.code || fromSearch.error) {
		return fromSearch;
	}

	const hash = location.hash.startsWith("#")
		? location.hash.slice(1)
		: location.hash;
	if (!hash) return fromSearch;

	return parseParamsString(hash);
}

export function stripAuthCallbackParamsFromUrl(): void {
	if (typeof window === "undefined") return;
	window.history.replaceState({}, document.title, window.location.pathname);
}

export function hasAuthCallbackParams(
	location: Pick<Location, "search" | "hash">,
): boolean {
	const parsed = parseAuthCallbackFromLocation(location);
	return Boolean(parsed.code || parsed.error);
}

export function extractAuthErrorMessage(err: unknown): string {
	const raw = (err as { data?: Record<string, unknown> })?.data;
	if (typeof raw?.message === "string" && raw.message.trim()) {
		return raw.message.trim();
	}
	if (err instanceof Error && err.message.trim()) {
		return err.message.trim();
	}
	return "Sign-in failed. Please try again.";
}
